import { useQuery } from '@apollo/client';
import { Button, Form, Input, Modal, Select } from 'antd';
import { capitalize } from 'lodash';
import { Key, ReactNode, ReactPortal, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  AnnotationDetection,
  GetFloorPlanWebQuery,
} from '../../../__generated__/graphql';
import { AREA_PARENT_TYPE, AREA_TYPES } from '../../../common/constants';
import { GET_AREA_TYPES } from '../graphql/queries';

const { Option } = Select;

type Detection = {
  bbox: number[];
  childID: string;
  childType: string;
  title: string;
  confidence: number;
  parentID: string | null;
  parentType: string | null;
};
type CurrentScaleType = {
  scale: number;
  positionX: number;
  positionY: number;
};
const AnnotationModal = ({
  visible,
  onCloseChildModal,
  onAddDetection,
  parentDetectionsData,
  onBoxClick,
  setVisible,
  newParents,
  setNewParents,
  updatedDetections,
}: {
  visible: boolean;
  parentDetectionsData: GetFloorPlanWebQuery;
  onCloseChildModal: () => void;
  onAddDetection: (data: Detection) => void;
  onBoxClick: (id: string | number) => void;
  setVisible: (arg0: boolean) => void;
  newParents: {
    title: string;
    parentID: string;
    parentType: string;
    bbox: (number | null)[];
  }[];
  setNewParents: (
    arg0: {
      title: string;
      parentID: string;
      parentType: string;
      bbox: (number | null)[];
    }[],
  ) => void;
  currentScale: CurrentScaleType;
  updatedDetections: (AnnotationDetection | null)[];
}) => {
  const [form] = Form.useForm();
  const [parentForm] = Form.useForm();
  const [isParentModalOpen, setIsParentModalOpen] = useState(false);
  const [showAddParentButton, setShowAddParentButton] = useState(false);
  const [selectedAreaType, setSelectedAreaType] = useState<string | null>(null);
  const [retainChildDetails, setRetainChildDetails] = useState(false);

  const { data: staticData } = useQuery(GET_AREA_TYPES);
  const staticAreaTypes = staticData?.getFloorPlanStaticDataWeb?.data || [];

  const selectedAreaTypeData = staticAreaTypes.find(
    (item) => item?.title === selectedAreaType,
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let allowedParentElements = selectedAreaTypeData?.allowedParentElements || [];

  useEffect(() => {
    if (!visible && !retainChildDetails) {
      form.resetFields();
    }
    allowedParentElements = selectedAreaTypeData?.allowedParentElements || [];
  }, [visible]);

  const options = staticAreaTypes.map((item) => ({
    label: capitalize(item?.title || ''),
    value: item?.title,
  }));

  const noChildParentAreaTypes = staticAreaTypes.filter(
    (type) =>
      (type?.type === AREA_PARENT_TYPE.PARENT ||
        type?.type === AREA_PARENT_TYPE.BOTH) &&
      ![AREA_TYPES.BED, AREA_TYPES.TOILET_SEAT, AREA_TYPES.ENTRANCE].includes(
        type.title?.toLowerCase() ?? '',
      ),
  );

  const parentAreaTypeOptions = noChildParentAreaTypes.map((item) => ({
    label: capitalize(item?.title || ''),
    value: item?.title,
  }));

  useEffect(() => {
    if (!isParentModalOpen) {
      parentForm?.resetFields();
    }
  }, [isParentModalOpen]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [allowedParents, setAllowedParent] = useState<any>([]);

  const showParentModal = () => {
    setRetainChildDetails(true);
    onCloseChildModal();
    setIsParentModalOpen(true);
  };

  const handleParentCancel = () => {
    setIsParentModalOpen(false);
  };

  const handleAddParent = (values: { title: string; parentType: string }) => {
    const newParent = {
      title: values?.title,
      parentID: uuidv4(),
      parentType: values?.parentType,
      bbox: [50, 50, 150, 150],
    };

    // Add to local state (for modal dropdown)
    setNewParents([...newParents, newParent]);

    // Also add as detection for rendering on image
    const parentDetection = {
      bbox: newParent.bbox,
      childID: newParent.parentID,
      childType: newParent.parentType,
      title: newParent.title,
      confidence: 100,
      parentID: null,
      parentType: null,
    };

    onAddDetection(parentDetection);

    setIsParentModalOpen(false);
    setVisible(true);
  };
  const handleSubmit = (values: {
    parent: string | null | undefined;
    childType: string;
    title: string;
  }) => {
    let selectedParent = null;

    if (values.parent) {
      selectedParent =
        parentDetectionsData?.getFloorPlanWeb?.annotation?.parentTypes?.find(
          (parent) => parent?.title === values.parent,
        );

      if (!selectedParent) {
        const parentDetection = updatedDetections.find(
          (detection) =>
            detection?.title === values.parent && !detection?.parentID,
        );

        if (parentDetection) {
          selectedParent = {
            title: parentDetection.title,
            parentID: parentDetection.childID,
            parentType: parentDetection.childType,
            bbox: parentDetection.bbox,
          };
        }
      }
    }

    const isParent = noChildParentAreaTypes?.filter((data) => {
      return data?.title === values?.childType;
    });

    const childId = uuidv4();
    if (isParent?.length > 0) {
      setNewParents([
        ...newParents,
        {
          title: values?.title,
          parentID: childId,
          parentType: values?.childType,
          bbox: [50, 50, 150, 150],
        },
      ]);
    }
    let newBbox;
    if (selectedParent?.bbox && selectedParent.bbox.length >= 2) {
      const [parentX, parentY] = selectedParent.bbox;
      newBbox = [parentX, parentY, (parentX || 0) + 100, (parentY || 0) + 100];
    } else {
      newBbox = [50, 50, 150, 150];
    }

    const newDetection = {
      bbox: newBbox || [50, 50, 150, 150],
      childID: childId,
      childType: values.childType,
      title: values?.title,
      confidence: 100,
      parentID: selectedParent?.parentID || null,
      parentType: selectedParent?.parentType || null,
    };
    onBoxClick(newDetection.childID);
    onAddDetection(newDetection as Detection);
    form.resetFields();
    onCloseChildModal();
  };

  useEffect(() => {
    const data =
      parentDetectionsData?.getFloorPlanWeb?.annotation?.parentTypes?.filter(
        (data) => {
          return selectedAreaTypeData?.allowedParentElements?.includes(
            data?.parentType as string,
          );
        },
      ) || [];

    const newParentsData = newParents?.filter((data) => {
      return selectedAreaTypeData?.allowedParentElements?.includes(
        data?.parentType as string,
      );
    });

    setAllowedParent([...data, ...newParentsData]);
  }, [selectedAreaType, newParents]);
  return (
    <>
      {visible && (
        <Modal
          className="add-child-annotation"
          title="Add New Area"
          open={visible}
          maskClosable={false}
          onCancel={() => {
            setRetainChildDetails(false);
            onCloseChildModal();
          }}
          footer={null}
        >
          <Form
            form={form}
            layout="vertical"
            className="add-child-annotation-form"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="title"
              label="Area Name"
              className="custom-label"
              rules={[
                { required: true, message: 'Please enter area name!' },
                { max: 50, message: 'Area name cannot exceed 50 characters!' },
                {
                  validator: (_, value) =>
                    value && value.trimStart() !== value
                      ? Promise.reject('Only spaces are not allowed!')
                      : Promise.resolve(),
                },
              ]}
            >
              <Input placeholder="Enter area name" />
            </Form.Item>

            <Form.Item
              name="childType"
              label="Area type"
              rules={[{ required: true, message: 'Please Select Area type!' }]}
              className="custom-label"
            >
              <Select
                placeholder="Area type"
                options={options}
                onChange={(value) => setSelectedAreaType(value)}
              />
            </Form.Item>

            {Array.isArray(allowedParents) && allowedParents?.length > 0 && (
              <>
                <Form.Item
                  name="parent"
                  label="Select Parent"
                  className={
                    selectedAreaTypeData?.title !== AREA_TYPES.BATHROOM
                      ? 'custom-label'
                      : ''
                  }
                  rules={[
                    {
                      required:
                        selectedAreaTypeData?.title !== AREA_TYPES.BATHROOM,
                      message: 'Please Select Parent Area!',
                    },
                  ]}
                >
                  <Select
                    placeholder="Select parent"
                    onChange={() => {
                      if (!showAddParentButton) {
                        setShowAddParentButton(true);
                      }
                    }}
                  >
                    {allowedParents?.map(
                      (
                        el: {
                          title:
                            | string
                            | number
                            | boolean
                            | Iterable<ReactNode>
                            | ReactPortal
                            | null
                            | undefined;
                        },
                        idx: Key | null | undefined,
                      ) => (
                        <Option key={idx} value={el?.title ?? ``}>
                          {capitalize((el?.title as string) || '')}
                        </Option>
                      ),
                    )}
                  </Select>
                </Form.Item>
                <Button
                  className="add-parent-btn"
                  type="link"
                  onClick={showParentModal}
                >
                  Add New Parent
                </Button>
              </>
            )}

            <Button
              type="primary"
              htmlType="submit"
              block
              className="submit-btn"
            >
              Submit
            </Button>
          </Form>
        </Modal>
      )}

      {/* Parent Modal */}
      <Modal
        className="add-parent-modal"
        title="Add Parent"
        open={isParentModalOpen}
        onCancel={handleParentCancel}
        footer={null}
        maskClosable={false}
      >
        <Form
          layout="vertical"
          form={parentForm}
          className="add-parent-form"
          initialValues={{ parentAreaType: 'common' }}
          onFinish={handleAddParent}
        >
          <Form.Item
            label="Parent area Name"
            name="title"
            className="custom-label"
          >
            <Input placeholder="Enter area name" />
          </Form.Item>

          <Form.Item
            name="parentType"
            label="Area type"
            rules={[{ required: true, message: 'Please Select Area type!' }]}
            className="custom-label"
          >
            <Select placeholder="Area type" options={parentAreaTypeOptions} />
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            Save Parent
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default AnnotationModal;
